import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { BookOpen, Target, Brain, Trophy, Clock, ChevronRight, LogOut, User, Zap, MessageCircle, X } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, correct: 0, exams: 0 });
  const [recentExams, setRecentExams] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState("feedback");
  const [feedbackMessage, setFeedbackMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [progressRes, historyRes] = await Promise.all([
        api.get("/progress"),
        api.get("/mock-exam/history")
      ]);

      const progress = progressRes.data;
      const total = progress.reduce((sum, p) => sum + (p.questions_attempted || 0), 0);
      const correct = progress.reduce((sum, p) => sum + (p.questions_correct || 0), 0);

      setStats({ total, correct, exams: historyRes.data.length });
      setRecentExams(historyRes.data.slice(0, 3));
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const quickActions = [
    { icon: BookOpen, title: "Practice Questions", desc: "100+ questions with explanations", path: "/exam-selection", color: "bg-blue-100 text-blue-600" },
    { icon: Clock, title: "Mock Exam", desc: "Timed exams up to 100 questions", path: "/exam-selection?mode=exam", color: "bg-green-100 text-green-600" },
    { icon: Brain, title: "AI Tutor", desc: "Get help from AI assistant", path: "/ai-tutor", color: "bg-purple-100 text-purple-600" },
    { icon: Target, title: "Flashcards", desc: "Quick review cards", path: "/exam-selection?mode=flashcards", color: "bg-orange-100 text-orange-600" },
  ];

  const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">LearnVault</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-slate-600">
              <User size={18} />
              <span className="hidden sm:inline text-sm">{user?.name}</span>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="btn-ghost flex items-center gap-2 text-slate-500"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Welcome */}
        <section className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Welcome back, {user?.name?.split(" ")[0]}!
          </h1>
          <p className="text-slate-600">Ready to continue your exam prep?</p>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-sm text-slate-500">Questions Done</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Zap className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{accuracy}%</p>
                <p className="text-sm text-slate-500">Accuracy</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.exams}</p>
                <p className="text-sm text-slate-500">Mock Exams</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.correct}</p>
                <p className="text-sm text-slate-500">Correct Answers</p>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, i) => (
              <button
                key={i}
                data-testid={`action-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => navigate(action.path)}
                className="card text-left hover:border-blue-300 group"
              >
                <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center mb-3`}>
                  <action.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{action.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{action.desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Recent Exams */}
        {recentExams.length > 0 && (
          <section>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Recent Mock Exams</h2>
              <button
                onClick={() => navigate("/progress")}
                className="text-blue-600 text-sm hover:underline flex items-center gap-1"
              >
                View All <ChevronRight size={16} />
              </button>
            </div>
            <div className="space-y-3">
              {recentExams.map((exam, i) => (
                <div key={i} className="card flex justify-between items-center">
                  <div>
                    <p className="font-medium text-slate-900">
                      {exam.exam_type} - {exam.discipline.replace(/_/g, " ")}
                    </p>
                    <p className="text-sm text-slate-500">
                      {exam.correct_answers}/{exam.total_questions} correct
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    exam.score_percentage >= 70 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {Math.round(exam.score_percentage)}%
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Floating Feedback Button */}
      <button
        data-testid="feedback-btn"
        onClick={() => setShowFeedback(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2 font-semibold z-40 hover:scale-105"
      >
        <MessageCircle size={20} />
        <span>Feedback</span>
      </button>

      {/* Feedback Modal */}
      {showFeedback && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">We'd Love Your Feedback!</h2>
              <button
                onClick={() => setShowFeedback(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={24} />
              </button>
            </div>

            {/* Feedback Type Selector */}
            <div className="flex gap-2 mb-4">
              {[
                { id: "feedback", label: "Feedback", emoji: "💬" },
                { id: "support", label: "Support", emoji: "🛟" },
                { id: "feature", label: "Feature Request", emoji: "✨" },
              ].map((type) => (
                <button
                  key={type.id}
                  onClick={() => setFeedbackType(type.id)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    feedbackType === type.id
                      ? "bg-blue-100 text-blue-700 border-2 border-blue-500"
                      : "bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200"
                  }`}
                >
                  <span className="mr-1">{type.emoji}</span> {type.label}
                </button>
              ))}
            </div>

            {/* Message Input */}
            <textarea
              value={feedbackMessage}
              onChange={(e) => setFeedbackMessage(e.target.value)}
              placeholder={
                feedbackType === "feedback"
                  ? "Tell us what you think about LearnVault..."
                  : feedbackType === "support"
                  ? "Describe the issue you're experiencing..."
                  : "What feature would you like to see?"
              }
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none resize-none"
            />

            {/* Submit Button */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowFeedback(false)}
                className="flex-1 py-3 px-4 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (feedbackMessage.trim()) {
                    // Open email client with pre-filled content
                    const subject = encodeURIComponent(
                      feedbackType === "feedback"
                        ? "LearnVault Feedback"
                        : feedbackType === "support"
                        ? "LearnVault Support Request"
                        : "LearnVault Feature Request"
                    );
                    const body = encodeURIComponent(
                      `${feedbackMessage}\n\n---\nFrom: ${user?.email || "Unknown"}\nType: ${feedbackType}`
                    );
                    window.open(`mailto:support@veosengineering.com?subject=${subject}&body=${body}`);
                    toast.success("Thank you for your feedback!");
                    setFeedbackMessage("");
                    setShowFeedback(false);
                  } else {
                    toast.error("Please enter a message");
                  }
                }}
                className="flex-1 py-3 px-4 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
              >
                Send
              </button>
            </div>

            <p className="text-xs text-slate-400 text-center mt-4">
              Your feedback helps us improve LearnVault for everyone
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
